import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Home, Bookmark, TrendingUp, Bell, User, LogOut } from 'lucide-react';
import { useFirebase } from '../contexts/FirebaseContext';
import { auth } from '../config/firebase';
import { getInitials } from '../utils/getInitials';

const RootLayout = () => {
  const location = useLocation();
  const { currentUser } = useFirebase();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!currentUser && location.pathname === '/auth') {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-gray-800 border-r border-gray-700">
        <div className="p-4">
          <h1 className="text-2xl font-bold text-gradient">NewsFlow</h1>
        </div>

        <nav className="mt-8">
          <Link
            to="/home"
            className={`flex items-center px-4 py-3 text-lg ${
              isActive('/home')
                ? 'text-purple-500 bg-purple-500/10'
                : 'text-gray-300 hover:bg-gray-700/50'
            }`}
          >
            <Home className="w-6 h-6 mr-3" />
            Home
          </Link>

          <Link
            to="/bookmarks"
            className={`flex items-center px-4 py-3 text-lg ${
              isActive('/bookmarks')
                ? 'text-purple-500 bg-purple-500/10'
                : 'text-gray-300 hover:bg-gray-700/50'
            }`}
          >
            <Bookmark className="w-6 h-6 mr-3" />
            Bookmarks
          </Link>

          <Link
            to="/trending"
            className={`flex items-center px-4 py-3 text-lg ${
              isActive('/trending')
                ? 'text-purple-500 bg-purple-500/10'
                : 'text-gray-300 hover:bg-gray-700/50'
            }`}
          >
            <TrendingUp className="w-6 h-6 mr-3" />
            Trending
          </Link>

          <Link
            to="/notifications"
            className={`flex items-center px-4 py-3 text-lg ${
              isActive('/notifications')
                ? 'text-purple-500 bg-purple-500/10'
                : 'text-gray-300 hover:bg-gray-700/50'
            }`}
          >
            <Bell className="w-6 h-6 mr-3" />
            Notifications
          </Link>
        </nav>

        {currentUser && (
          <>
            {/* User Profile */}
            <div className="absolute bottom-20 left-0 right-0 p-4">
              <div className="flex items-center space-x-3 px-4 py-3 rounded-lg hover:bg-gray-700/50">
                <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-medium">
                  {getInitials(currentUser.displayName || currentUser.email?.split('@')[0])}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {currentUser.displayName || currentUser.email?.split('@')[0]}
                  </p>
                  <p className="text-sm text-gray-400 truncate">
                    @{currentUser.email?.split('@')[0]}
                  </p>
                </div>
              </div>
            </div>

            {/* Logout Button */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <button
                onClick={handleLogout}
                className="flex items-center px-4 py-3 text-lg text-gray-300 hover:bg-gray-700/50 w-full rounded-lg"
              >
                <LogOut className="w-6 h-6 mr-3" />
                Logout
              </button>
            </div>
          </>
        )}
      </aside>

      {/* Main Content */}
      <main className="ml-64">
        <div className="max-w-2xl mx-auto py-4">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default RootLayout;
